import StringComponent from "csi/components/string/StringComponent";
import { useFormStore, useStatefulFormField } from "csi/middleware/form/index";
import { useLocale } from "designSystem/locale";
import { observer } from "mobx-react-lite";
import React, { ChangeEvent, FunctionComponent } from "react";
import { EMPTY_READONLY } from "../constants";
import { useComponentOverlayContext } from "../hooks";

const FormFieldString: FunctionComponent = observer(() => {
	const formStore = useFormStore();
	const field = useStatefulFormField();
	const isParentOverlay = useComponentOverlayContext();
	const {
		attributeExpression,
		format,
		id,
		label,
		tooltip,
		transientInfo: {
			disabled,
			expressionDisabled,
			expressionValue,
			readOnly,
			required,
			value,
		},
	} = field;
	const [fieldInvalid, fieldMessage] = formStore?.getFieldError(id) || [false, ""];
	const loc = useLocale();

	const handleChangeNoUpdate = (value?: string, event?: ChangeEvent<HTMLElement>): void => {
		formStore?.handleFieldChange(event, field, null, true);
	};

	const handleExpression = (): void => {
		field.transientInfo.expressionDisabled = !field.transientInfo.expressionDisabled;

		// user restores the expression
		if (!field.transientInfo.expressionDisabled) {
			field.transientInfo.value = field.transientInfo.expressionValue;
		}

		formStore?.updateForm(field);
	};

	const handleUpdate = (): void => {
		formStore?.updateForm(field);
	};

	let valueString = "";
	if (value) {
		valueString = value as string;
	} else if (readOnly) {
		valueString = loc.resource(EMPTY_READONLY);
	}

	return (
		<StringComponent
			aria-label={label}
			dataCsiAutomation={id}
			disabled={disabled}
			error={fieldInvalid}
			errorMessage={fieldMessage}
			expressionButtonProps={
				attributeExpression && {
					expressionActive: !expressionDisabled,
					expressionValue,
					onChangeExpressionState: attributeExpression ? handleExpression : null,
				}
			}
			format={format}
			helperText={fieldMessage}
			label={label}
			onBlur={handleUpdate}
			onChange={handleChangeNoUpdate}
			readOnly={readOnly}
			required={required}
			tabIndex={isParentOverlay ? -1 : undefined}
			tooltip={tooltip}
			type={readOnly ? "readonly" : "text"}
			value={valueString}
		/>
	);
});

export default FormFieldString;